// Generic form renderer for platform-config namespaces. Iterates a
// NamespaceSchema and emits the right input per Field.kind, hooking each
// into a controlled state object the parent owns.
//
// Save semantics: parent collects the form values and merges into the
// original server-side blob — we never strip unknown keys, so any field
// added via the legacy raw-JSON editor sticks around even if it isn't in
// the schema yet.

import { useMemo } from 'react';
import type {
  Field,
  FieldSection,
  NamespaceSchema,
} from '@/lib/configs/types';

export type SchemaValues = Record<string, unknown>;

/**
 * Field-level validation errors keyed by Zod field path
 * (e.g. `min_password_length`, `contact.phone`). Comes from the API's 422
 * response body via `flatten().fieldErrors`.
 */
export type FieldErrors = Record<string, string[]>;

interface SchemaFormProps {
  schema: NamespaceSchema;
  values: SchemaValues;
  onChange: (next: SchemaValues) => void;
  disabled?: boolean;
  errors?: FieldErrors;
}

// Walk a dotted path and return the value at it, or undefined.
function get(obj: SchemaValues, key: string): unknown {
  const parts = key.split('.');
  let cur: unknown = obj;
  for (const p of parts) {
    if (!cur || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[p];
  }
  return cur;
}

// Immutably set a value at a dotted path, creating intermediate objects.
function set(obj: SchemaValues, key: string, value: unknown): SchemaValues {
  const parts = key.split('.');
  if (parts.length === 1) {
    return { ...obj, [parts[0]!]: value };
  }
  const [head, ...rest] = parts;
  const child = (obj[head!] as SchemaValues | undefined) ?? {};
  return { ...obj, [head!]: set(child, rest.join('.'), value) };
}

// Some config fields are localized as a {en,fr,ar,es} map (e.g. site-settings
// footer tagline, CTA labels). The form edits the `en` value here and preserves
// the other locales (authored via the legacy JSON editor); the storefront's
// pickLocalized() reads either a plain string or this map.
const LOCALE_KEYS = ['en', 'fr', 'ar', 'es'] as const;
function isLocalizedObject(v: unknown): v is Record<string, unknown> {
  return (
    typeof v === 'object' &&
    v !== null &&
    !Array.isArray(v) &&
    LOCALE_KEYS.some((k) => k in (v as Record<string, unknown>))
  );
}

function asString(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (isLocalizedObject(v)) {
    if (typeof v.en === 'string') return v.en;
    for (const k of LOCALE_KEYS) if (typeof v[k] === 'string') return v[k] as string;
    return '';
  }
  return String(v);
}

// Write-back for text inputs: keep a localized map intact (update only `en`),
// otherwise emit a plain value.
function emitText(orig: unknown, next: string, blankToUndefined = false): unknown {
  if (isLocalizedObject(orig)) return { ...orig, en: next };
  return blankToUndefined ? next || undefined : next;
}

function asNumberInput(v: unknown): string {
  if (v == null || v === '') return '';
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  if (typeof v === 'string' && v.trim() !== '') return v;
  return '';
}

function asBoolean(v: unknown): boolean {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'string') return v === 'true' || v === '1';
  if (typeof v === 'number') return v !== 0;
  return false;
}

function asStringArray(v: unknown): string[] {
  if (Array.isArray(v)) return v.map(String);
  if (typeof v === 'string' && v) return [v];
  return [];
}

const inputBase =
  'w-full rounded-md border bg-white px-2.5 py-1.5 text-[13px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-ink)] disabled:cursor-not-allowed disabled:opacity-50';

const inputBorderOk = 'border-[color:var(--bo-line)]';
const inputBorderErr = 'border-[#dc2626]';

function FieldRow({
  field,
  value,
  onChange,
  disabled,
  error,
}: {
  field: Field;
  value: unknown;
  onChange: (next: unknown) => void;
  disabled: boolean;
  error?: string;
}) {
  const id = `cfg-${field.key.replace(/\./g, '-')}`;

  // Boolean renders inline (checkbox + label) — different layout from the
  // labelled-row pattern below.
  if (field.kind === 'boolean') {
    return (
      <div className="flex items-start gap-2.5">
        <input
          id={id}
          type="checkbox"
          checked={asBoolean(value)}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          className={`mt-0.5 h-4 w-4 rounded border text-[color:var(--bo-ink)] focus:ring-1 focus:ring-[color:var(--bo-ink)] disabled:cursor-not-allowed disabled:opacity-50 ${
            error ? 'border-[#dc2626]' : 'border-[color:var(--bo-line)]'
          }`}
        />
        <label htmlFor={id} className="text-[12.5px]">
          <div className="font-medium text-[color:var(--bo-ink)]">{field.label}</div>
          {field.description ? (
            <div className="mt-0.5 text-[11.5px] text-[color:var(--bo-mute)]">
              {field.description}
            </div>
          ) : null}
          {error ? (
            <div className="mt-0.5 text-[11.5px] text-[#9f1239]">{error}</div>
          ) : null}
        </label>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-[12px] font-medium text-[color:var(--bo-ink)]">
        {field.label}
      </label>
      {field.kind === 'textarea' ? (
        <textarea
          id={id}
          value={asString(value)}
          rows={field.rows ?? 3}
          placeholder={field.placeholder}
          disabled={disabled}
          onChange={(e) => onChange(emitText(value, e.target.value))}
          className={`${inputBase} ${error ? inputBorderErr : inputBorderOk} font-mono text-[12.5px]`}
        />
      ) : field.kind === 'number' ? (
        <input
          id={id}
          type="number"
          value={asNumberInput(value)}
          min={field.min}
          max={field.max}
          step={field.step}
          placeholder={field.placeholder}
          disabled={disabled}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === '') onChange(undefined);
            else {
              const n = Number(raw);
              onChange(Number.isFinite(n) ? n : undefined);
            }
          }}
          className={`${inputBase} ${error ? inputBorderErr : inputBorderOk} tabular-nums`}
        />
      ) : field.kind === 'select' ? (
        <select
          id={id}
          value={asString(value) || (field.default ?? '')}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value || undefined)}
          className={`${inputBase} ${error ? inputBorderErr : inputBorderOk}`}
        >
          {field.default == null ? <option value="">—</option> : null}
          {field.options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      ) : field.kind === 'multiselect' ? (
        <div className={`flex flex-wrap gap-1.5 rounded-md border bg-white p-2 ${error ? inputBorderErr : inputBorderOk}`}>
          {field.options.map((opt) => {
            const selected = asStringArray(value).includes(opt.value);
            return (
              <label
                key={opt.value}
                className={`inline-flex cursor-pointer items-center gap-1.5 rounded-md border px-2 py-1 text-[11.5px] transition ${
                  selected
                    ? 'border-[color:var(--bo-ink)] bg-[color:var(--bo-ink)] text-white'
                    : 'border-[color:var(--bo-line)] text-[color:var(--bo-ink)] hover:border-[color:var(--bo-mute)]'
                } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={selected}
                  disabled={disabled}
                  onChange={(e) => {
                    const cur = asStringArray(value);
                    if (e.target.checked) onChange([...cur, opt.value]);
                    else onChange(cur.filter((v) => v !== opt.value));
                  }}
                  className="hidden"
                />
                {opt.label}
              </label>
            );
          })}
        </div>
      ) : (
        <input
          id={id}
          type={field.kind}
          value={asString(value)}
          placeholder={field.placeholder}
          required={field.kind === 'text' && field.required}
          disabled={disabled}
          autoComplete={field.kind === 'password' ? 'new-password' : 'off'}
          onChange={(e) => onChange(emitText(value, e.target.value, true))}
          className={`${inputBase} ${error ? inputBorderErr : inputBorderOk}`}
        />
      )}
      {error ? (
        <p className="text-[11.5px] text-[#9f1239]">{error}</p>
      ) : field.description ? (
        <p className="text-[11.5px] text-[color:var(--bo-mute)]">{field.description}</p>
      ) : null}
    </div>
  );
}

function Section({
  section,
  values,
  onChange,
  disabled,
  errors,
}: {
  section: FieldSection;
  values: SchemaValues;
  onChange: (next: SchemaValues) => void;
  disabled: boolean;
  errors?: FieldErrors;
}) {
  return (
    <fieldset className="space-y-3">
      <legend className="text-[12px] font-semibold uppercase tracking-[0.06em] text-[color:var(--bo-mute)]">
        {section.label}
      </legend>
      {section.description ? (
        <p className="text-[12px] text-[color:var(--bo-mute)]">{section.description}</p>
      ) : null}
      <div className="grid grid-cols-1 gap-3.5 md:grid-cols-2">
        {section.fields.map((f) => (
          <div
            key={f.key}
            className={f.kind === 'textarea' || f.kind === 'multiselect' ? 'md:col-span-2' : ''}
          >
            <FieldRow
              field={f}
              value={get(values, f.key)}
              onChange={(next) => onChange(set(values, f.key, next))}
              disabled={disabled}
              error={errors?.[f.key]?.[0]}
            />
          </div>
        ))}
      </div>
    </fieldset>
  );
}

export function SchemaForm({ schema, values, onChange, disabled = false, errors }: SchemaFormProps) {
  // Stable key list so React doesn't rekey sections on every render.
  const sectionKeys = useMemo(
    () => schema.sections.map((s) => s.label),
    [schema],
  );
  return (
    <div className="space-y-6">
      {schema.sections.map((section, idx) => (
        <Section
          key={sectionKeys[idx]}
          section={section}
          values={values}
          onChange={onChange}
          disabled={disabled}
          errors={errors}
        />
      ))}
    </div>
  );
}
